import React, { useState, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import { Route, Routes, Link, Navigate, Router, useLocation } from 'react-router-dom';
import { withStyles } from '@mui/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import { MdClose } from 'react-icons/md';
import cx from 'classnames';
import PageFooter from '../containers/PageFooter';
import history from '../history';
import { Register, Details, CallBack, TermsAndConditions } from './index';
import actions from '../actions';
import * as selectors from '../selectors';
import Status from "./Status/Status";

const styles = ({ palette, breakpoints }) => ({
  root: {
    flexGrow: 1,
  },
  companyName: {
    flexGrow: 1,
    color: '#000', // Hardcoding the company name to black as it will be on a white headerbar.
  },
  actionBtn: {
    marginLeft: '10px',
    color: palette.primary.main,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
    border: 'none',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    borderRadius: '6px',
    transition: 'background 0.2s, color 0.2s, border 0.2s',
    '&:hover': {
      backgroundColor: `${palette.primary.main} !important`,
      color: palette.primary.contrastText,
      border: 'none',
    },
    [breakpoints.down('sm')]: {
      marginLeft: '0px',
    },
  },
  currentPath: {
    border: `1px solid ${palette.primary.main} !important`,
    color: palette.primary.main,
    backgroundColor: palette.primary.contrastText,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
    borderRadius: '6px',
    boxShadow: 'none',
  },
  pageContainer: {
    minHeight: '77vh',
  },
  notification: {
    display: 'flex',
    minHeight: '7vh',
  },
  flexCentered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  blackCircle: {
    width: '5px',
    height: '5px',
    backgroundColor: '#000000',
  },
  logoContainer: {
    flexGrow: 1,
    resizeMode: 'contain',
  },
  headerBar: {
    // backgroundColor: '#fff', // Removed to use theme's primary color
  },
});

const HistoryRouter = ({ basename, children, history }) => {
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });

  useLayoutEffect(() => history.listen(setState), [history]);

  return (
    <Router
      basename={basename}
      location={state.location}
      navigationType={state.action}
      navigator={history}
    >
      {children}
    </Router>
  );
};

const NavLinks = withStyles(styles)(
  ({ accountDetails, classes, isLoggedIn, logout, logo }) => {
    const { pathname } = useLocation();
    return (
      <Toolbar color="default">
        {logo ? (
          <div className={classes.logoContainer}>
            <img style={{  maxHeight: "70px", objectFit: "contain" }} src={logo} alt="Logo" />
          </div>
        ) : (
          <Typography variant="h6" className={cx('noselect', classes.companyName)}>
            {(accountDetails &&
              accountDetails.extensionData &&
              accountDetails.extensionData.extensionData &&
              accountDetails.extensionData.extensionData.StaticContent &&
              accountDetails.extensionData.extensionData.StaticContent.companyname) ||
              accountDetails.name}
          </Typography>
        )}
        {isLoggedIn && (
          <div style={{ display: 'flex', gap: '16px' }}>
            <Button
              component={Link}
              to="/"
              variant="text"
              color="primary"
              className={cx(classes.actionBtn, { [classes.currentPath]: pathname === '/' })}
            >
              HOME
            </Button>
            <Button
              component={Link}
              to="/details"
              variant="text"
              color="primary"
              className={cx(classes.actionBtn, { [classes.currentPath]: pathname === '/details' })}
            >
              DETAILS
            </Button>
            <Button
              variant="text"
              color="primary"
              className={classes.actionBtn}
              onClick={logout}
            >
              LOG OUT
            </Button>
          </div>
        )}
      </Toolbar>
    );
  }
);

const Notification = withStyles(styles)(({ classes, closeNotification, details }) => (
  <Paper className={classes.notification}>
    <div style={{ flex: 1 }} className={classes.flexCentered} />
    <div style={{ flex: 8 }} className={classes.flexCentered}>
      <div style={{ marginRight: '5px' }} className={classes.blackCircle} />
      <Typography>
        Hi {details.name.first || ''}, you can start editing your details and hit the &apos;Submit&apos;
        button to send it to us
      </Typography>
    </div>
    <div style={{ flex: 1 }} className={classes.flexCentered}>
      <IconButton onClick={closeNotification}>
        <MdClose style={{ color: 'black', fontSize: 24 }} />
      </IconButton>
    </div>
  </Paper>
));

function RootComponent({
  details,
  classes,
  accountDetails,
  rehydrated,
  isLoggedIn,
  logout,
  showNotification,
  closeNotification,
  logos,
}) {

  const location = useLocation();
  const { pathname } = location;
  const iframe = pathname === '/iframe';

  return (
    <div className={classes.root}>
      {!iframe && (
        <AppBar position="static" style={{ backgroundColor: 'white' }} className={classes.headerBar}>
          <NavLinks
            logo={logos}
            accountDetails={accountDetails}
            isLoggedIn={isLoggedIn}
            logout={logout}
          />
        </AppBar>
      )}
      {!iframe && pathname === '/details' && showNotification && (
        <Notification closeNotification={closeNotification} details={details} />
      )}
      <div className={classes.pageContainer}>
        {rehydrated && (
          <Routes>
            <Route path="/:company/:id" element={<Register />} />
            <Route path="/details" element={isLoggedIn ? <Details /> : <Navigate to="/" replace />} />
            <Route path="/t&c" element={<TermsAndConditions />} />
            <Route path="/callback" element={<CallBack />} />
            <Route path="/iframe" element={<Register iframe />} />
            <Route path="/status" element={<Status />} />
            <Route path="/" element={<Register />} />
          </Routes>
        )}
      </div>
      {!iframe && accountDetails.name && <PageFooter accountDetails={accountDetails} />}
    </div>
  );
}

const ConnectedRoot = connect(
  (state, _) => ({
    details: selectors.getDetails(state),
    isLoggedIn: selectors.getIsLoggedIn(state),
    rehydrated: selectors.getRehydrated(state),
    accountDetails: selectors.getAccountDetails(state) || { address: {} },
    loading: selectors.getAppLoading(state),
    showNotification: selectors.getShowNotification(state),
    validated: selectors.getValidated(state),
  }),
  (dispatch, _) => ({
    logout() {
      dispatch(actions.app.auth.logout());
    },
    closeNotification() {
      dispatch(actions.app.appState.setShowNotification(false));
    },
  }),
)(withStyles(styles)(RootComponent));

function Root(props) {
  return (
    <HistoryRouter history={history}>
      <ConnectedRoot {...props} />
    </HistoryRouter>
  );
}

export default Root;
